/** Workman global date formatting — DD-MM-YYYY (per UI mandate 2026-05-09).
 *
 * Use these helpers everywhere. Do not call `toLocaleDateString` / build
 * date strings inline — `DD-MM-YYYY` is the canonical display format
 * across every page, drawer, dialog, chip subtitle, and detail surface.
 */

import { moneyDecimals } from "@/shared/store/moneyFormat";

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Format a Date or ISO string as `DD-MM-YYYY`. Accepts:
 * - Date instance
 * - ISO datetime ("2026-05-09T12:30:00Z")
 * - ISO date-only ("2026-05-09") — parsed as local date (no timezone shift).
 *
 * Returns `"—"` for null / empty / invalid input.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Date-only ISO — parse as local to avoid TZ rolling backward.
    const parts = value.split("-").map(Number);
    d = new Date(parts[0]!, parts[1]! - 1, parts[2]!);
  } else {
    d = new Date(value);
  }
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** Format ISO datetime as `DD-MM-YYYY HH:MM AM/PM`. Returns "—" for null/empty. */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  let h = d.getHours();
  const m = pad(d.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${date} ${pad(h)}:${m} ${ampm}`;
}

/** Time-only — `HH:MM AM/PM`. */
export function formatTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  let h = d.getHours();
  const m = pad(d.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${pad(h)}:${m} ${ampm}`;
}

/** Indian-rupee money — `₹1,23,457` or `₹1,23,456.78`. One formatter for
 *  every payroll surface (the module used to carry a dozen local helpers with
 *  0/2/3-decimal drift). Precision follows the org-wide Rupees / Paisa
 *  setting (`money_display`, applied by `<MoneyPrecisionScope>`, default whole
 *  rupees) unless `decimals` is passed. Negative values render with a leading minus: `-₹500`.
 *  `signed` forces an explicit `+` on positives (ledger credits/debits). */
export function formatINR(
  value: string | number | null | undefined,
  opts: { decimals?: number; signed?: boolean; dash?: boolean } = {},
): string {
  const { signed = false, dash = false } = opts;
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (dash && n === 0) return "—";
  const abs = formatAmount(Math.abs(n), opts.decimals);
  if (n < 0) return `-₹${abs}`;
  return `${signed && n > 0 ? "+" : ""}₹${abs}`;
}

/** Bare money figure without the `₹` sign (`1,23,457` / `1,23,456.78`) for
 *  cells whose column header already says ₹. Same precision rule as
 *  `formatINR`. */
export function formatAmount(value: number, decimals: number = moneyDecimals()): string {
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Payroll month label from an ISO date (first of month) or `YYYY-MM` —
 *  `September 2026` (long) / `Sep 2026` (short). Parsed as a local date so a
 *  date-only string never rolls back a day. */
export function formatMonth(
  value: string | null | undefined,
  style: "long" | "short" = "long",
): string {
  if (!value) return "—";
  const m = /^(\d{4})-(\d{2})/.exec(value);
  if (!m) return "—";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { month: style, year: "numeric" });
}

/** File size — `512 B`, `4.2 KB`, `1.3 MB`, `2.05 GB`. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
